/**
 * Cognito User Service
 *
 * This service handles user management using Amplify Gen 2 Auth APIs
 * Implements proper user management patterns for Amplify Gen 2
 */

import { fetchAuthSession, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

export interface CognitoUser {
  id: string;
  email: string;
  fullName?: string;
  groups: string[];
  customAttributes: {
    isSuperAdmin?: string;
    isAdmin?: string;
    isJudge?: string;
    assignedCategories?: string;
  };
  isActive: boolean;
  createdAt?: string;
  lastModified?: string;
}

export interface ListUsersResponse {
  users: CognitoUser[];
  nextToken?: string;
}

class CognitoUserService {
  /**
   * List all users from Cognito User Pool
   * Uses Application model to get user information indirectly
   * Note: Direct Cognito admin access requires backend APIs
   */
  async listUsers(nextToken?: string): Promise<ListUsersResponse> {
    try {
      // Use getUsersByGroup with 'all' to get all users
      const users = await this.getUsersByGroup('all');
      
      // Simple pagination simulation (not ideal for production)
      const limit = 20;
      const start = nextToken ? parseInt(nextToken) : 0;
      const end = start + limit;
      const paginatedUsers = users.slice(start, end);
      const hasMore = end < users.length;

      return {
        users: paginatedUsers,
        nextToken: hasMore ? end.toString() : undefined
      };
    } catch (error) {
      console.error('Error listing users from Cognito:', error);
      throw error;
    }
  }

  /**
   * List users with specific group membership
   * Uses getUsersByGroup with pagination
   */
  async listUsersByGroup(groupName: string, nextToken?: string): Promise<ListUsersResponse> {
    try {
      // Get users for the specific group
      const users = await this.getUsersByGroup(groupName);
      
      // Simple pagination simulation
      const limit = 20;
      const start = nextToken ? parseInt(nextToken) : 0;
      const end = start + limit;
      const paginatedUsers = users.slice(start, end);
      const hasMore = end < users.length;

      return {
        users: paginatedUsers,
        nextToken: hasMore ? end.toString() : undefined
      };
    } catch (error) {
      console.error(`Error listing users for group ${groupName}:`, error);
      throw error;
    }
  }

  /**
   * Get users with judge role specifically
   */
  async getJudgeUsers(): Promise<CognitoUser[]> {
    return this.getUsersByGroup('JUDGE');
  }

  /**
   * Get users by group name
   * Uses Lambda function with proper Cognito admin access to fetch users from specific groups
   */
  async getUsersByGroup(groupName: string): Promise<CognitoUser[]> {
    try {
      console.log(`🔍 Getting users for group: ${groupName}`);
      
      const client = generateClient<Schema>();
      
      // Call the list-users Lambda function with proper Cognito admin access
      console.log(`📞 Calling listUsers Lambda with group: ${groupName}`);
      const lambdaResult = await client.queries.listUsers({
        group: groupName,
        limit: '50'
      });

      console.log(`📋 Lambda result:`, lambdaResult);

      if (!lambdaResult.data) {
        console.warn(`No data returned from listUsers Lambda for group: ${groupName}`);
        return [];
      }

      // Parse the Lambda response
      let usersData;
      try {
        // The Lambda returns a JSON string, so we need to parse it
        usersData = typeof lambdaResult.data === 'string' ? 
          JSON.parse(lambdaResult.data) : 
          lambdaResult.data;
      } catch (parseError) {
        console.error('Error parsing Lambda response:', parseError);
        console.error('Raw response:', lambdaResult.data);
        return [];
      }

      // Check if there's an error in the response
      if (usersData.error) {
        console.error('Lambda returned error:', usersData.error, usersData.message);
        return [];
      }

      const users = usersData.users || [];
      console.log(`✅ Found ${users.length} users in group ${groupName}`);
      console.log(`👥 User data:`, users);

      // Convert Lambda response to CognitoUser format
      const cognitoUsers: CognitoUser[] = users.map((user: {
        id?: string;
        email: string;
        fullName?: string;
        groups?: string[];
        customAttributes?: Record<string, string>;
        isActive?: boolean;
        createdAt?: string;
        lastModified?: string;
      }) => ({
        id: user.id || user.email,
        email: user.email,
        fullName: user.fullName || '',
        groups: user.groups || [],
        customAttributes: user.customAttributes || {},
        isActive: user.isActive !== false, // Default to true if not specified
        createdAt: user.createdAt,
        lastModified: user.lastModified
      }));

      return cognitoUsers;

    } catch (error) {
      console.error(`❌ Error getting users for group ${groupName}:`, error);
      console.error(`❌ Error details:`, {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Return empty array instead of crashing when Lambda function fails
      return [];
    }
  }

  /**
   * Update user's custom attributes
   * Uses Amplify Auth updateUserAttributes for the current user
   */
  async updateUserAttributes(username: string, attributes: Record<string, string>): Promise<void> {
    try {
      // Get current user to verify they can update their own attributes
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('User not authenticated');
      }

      const userAttributes = await fetchUserAttributes();
      const currentUserEmail = userAttributes.email;

      // For security, only allow users to update their own attributes
      // or implement proper admin checks here
      if (currentUserEmail !== username) {
        // This would typically require admin privileges
        // For now, we'll implement basic validation
        console.warn(`Attempting to update attributes for different user: ${username}`);
      }

      // Convert attributes to the format expected by Amplify
      const amplifyAttributes: Record<string, string> = {};
      
      for (const [key, value] of Object.entries(attributes)) {
        // Map internal attribute names to Cognito format
        switch (key) {
          case 'isSuperAdmin':
            amplifyAttributes['custom:isSuperAdmin'] = value;
            break;
          case 'isAdmin':
            amplifyAttributes['custom:isAdmin'] = value;
            break;
          case 'isJudge':
            amplifyAttributes['custom:isJudge'] = value;
            break;
          case 'assignedCategories':
            amplifyAttributes['custom:assignedCategories'] = value;
            break;
          case 'fullName':
            amplifyAttributes['name'] = value;
            break;
          default:
            // For other attributes, use as-is if they start with 'custom:'
            if (key.startsWith('custom:')) {
              amplifyAttributes[key] = value;
            } else {
              amplifyAttributes[key] = value;
            }
        }
      }

      // Update user attributes using Amplify Auth
      await updateUserAttributes({
        userAttributes: amplifyAttributes
      });

      console.log(`Successfully updated attributes for user: ${username}`);
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  }

  /**
   * Assign categories to a judge
   * Updates the judge's custom attributes with assigned categories
   */
  async assignCategoriesToJudge(judgeEmail: string, categories: string[]): Promise<void> {
    try {
      // Get current user to verify permissions
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('User not authenticated');
      }

      const userAttributes = await fetchUserAttributes();
      const currentUserEmail = userAttributes.email;

      // For self-assignment or admin operations
      if (currentUserEmail === judgeEmail) {
        // User is updating their own categories
        await this.updateUserAttributes(judgeEmail, {
          assignedCategories: JSON.stringify(categories),
          isJudge: 'true'
        });
      } else {
        // This would require admin privileges in a real implementation
        // For now, we'll just log the operation
        console.warn(`Admin operation: Assigning categories to ${judgeEmail}:`, categories);
        
        // In a production app, you'd need to:
        // 1. Verify current user has admin privileges
        // 2. Use a backend API with Cognito admin permissions
        // 3. Update the target user's attributes
        
        throw new Error('Cross-user category assignment requires admin backend API');
      }

      console.log(`Successfully assigned categories to judge ${judgeEmail}:`, categories);
    } catch (error) {
      console.error('Error assigning categories to judge:', error);
      throw error;
    }
  }

  /**
   * Get user's Cognito groups by email
   * Uses Amplify Auth to fetch user attributes and session data
   */
  async getUserGroups(email: string): Promise<string[]> {
    try {
      // Get the current auth session to access user groups
      const session = await fetchAuthSession();
      
      // Check if user is authenticated
      if (!session.tokens) {
        console.warn('No auth session available');
        return [];
      }

      // Get groups from JWT token
      const accessToken = session.tokens.accessToken;
      if (accessToken && typeof accessToken.payload === 'object' && accessToken.payload !== null) {
        const payload = accessToken.payload as { 'cognito:groups'?: string[] };
        const groups = payload['cognito:groups'] || [];
        
        // Map Cognito group names to our internal group names
        return groups.map((group: string) => {
          switch (group) {
            case 'SUPER_ADMIN':
              return 'SUPER_ADMIN';  // Keep consistent with USER_GROUPS constant
            case 'ADMIN':
              return 'ADMIN';        // Keep consistent with USER_GROUPS constant
            case 'JUDGE':
              return 'JUDGE';        // Keep consistent with USER_GROUPS constant
            default:
              return group.toLowerCase();
          }
        });
      }

      // Fallback: try to get user attributes
      try {
        const userAttributes = await fetchUserAttributes();
        const userEmail = userAttributes.email;
        
        // Only return groups if the email matches the requested email
        if (userEmail === email) {
          // Check custom attributes for role information
          const roles: string[] = [];
          if (userAttributes['custom:isSuperAdmin'] === 'true') {
            roles.push('super-admin');
          }
          if (userAttributes['custom:isAdmin'] === 'true') {
            roles.push('admin');
          }
          if (userAttributes['custom:isJudge'] === 'true') {
            roles.push('judge');
          }
          return roles;
        }
      } catch (attributeError) {
        console.warn('Could not fetch user attributes:', attributeError);
      }

      return [];
    } catch (error) {
      console.error('Error getting user groups:', error);
      return [];
    }
  }
}

export const cognitoUserService = new CognitoUserService();
